<template>
  <section class="signage-reveal" aria-labelledby="signage-reveal-title">
    <div class="container">
      <div class="signage-reveal__image">
        <img src="../../assets/images/Newlandindpage/Group 27919@2x.png" alt="" loading="lazy" />
      </div>
      <div ref="track" class="signage-reveal__track">
        <div class="signage-reveal__sticky">
          <span class="signage-reveal__rule" aria-hidden="true"></span>
          <h2 id="signage-reveal-title" class="signage-reveal__title" :aria-label="fullTitle">
            <span aria-hidden="true">
              <span v-for="(word, index) in words" :key="`${$i18n.locale}-${index}`"
                class="signage-reveal__word" :class="{ 'signage-reveal__word--accent': word.accent, 'signage-reveal__word--space': !word.text.trim() }"
                :style="{ '--lit': '0%', '--word-opacity': 0.35, '--word-y': '6px' }">{{ word.text }}</span>
            </span>
          </h2>
        </div>
      </div>
      <div class="signage-reveal__details">
        <article v-for="feature in features" :key="feature.key" class="signage-reveal__feature">
          <Icon :name="feature.icon" size="24" icon-class="signage-reveal__icon" aria-hidden="true" />
          <div>
            <h3>{{ $t(`AboutFour.${feature.key}_title`) }}</h3>
            <p>{{ $t(`AboutFour.${feature.key}_desc`) }}</p>
          </div>
        </article>
      </div>
    </div>
  </section>
</template>

<script>
import Icon from '../icon/Icon'

export default {
  name: 'SignageReveal',
  components: { Icon },
  data() {
    return {
      features: [
        { key: 'signage_station', icon: 'map-pin' },
        { key: 'signage_app', icon: 'smartphone' }
      ]
    }
  },
  computed: {
    phrases() {
      return [
        { text: this.$t('AboutFour.title_before'), accent: false },
        { text: this.$t('AboutFour.title_strong1'), accent: true },
        { text: this.$t('AboutFour.title_look'), accent: false },
        { text: this.$t('AboutFour.title_after'), accent: true }
      ]
    },
    fullTitle() {
      return this.phrases.map(phrase => phrase.text).join(' ')
    },
    words() {
      const segmenter = typeof Intl !== 'undefined' && Intl.Segmenter
        ? new Intl.Segmenter(this.$i18n.locale, { granularity: 'word' })
        : null
      return this.phrases.reduce((words, phrase, index) => {
        const text = phrase.text + (index < this.phrases.length - 1 ? ' ' : '')
        const segments = segmenter
          ? Array.from(segmenter.segment(text), item => item.segment)
          : text.split(/(\s+)/)
        segments.forEach(text => {
          const last = words[words.length - 1]
          // Keep punctuation on the preceding word so a line never starts with "," or "."
          if (last && /^[^\s\p{L}\p{N}]+$/u.test(text) && !/\s$/.test(last.text)) {
            last.text += text
          } else {
            words.push({ text, accent: phrase.accent })
          }
        })
        return words
      }, [])
    }
  },
  watch: {
    '$i18n.locale'() {
      this.$nextTick(() => {
        this.collectWords()
        this.queueReveal()
      })
    }
  },
  mounted() {
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    this.collectWords()
    this.motionQuery.addEventListener
      ? this.motionQuery.addEventListener('change', this.queueReveal)
      : this.motionQuery.addListener(this.queueReveal)
    window.addEventListener('scroll', this.queueReveal, { passive: true })
    window.addEventListener('resize', this.queueReveal)
    this.queueReveal()
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.queueReveal)
    window.removeEventListener('resize', this.queueReveal)
    this.motionQuery.removeEventListener
      ? this.motionQuery.removeEventListener('change', this.queueReveal)
      : this.motionQuery.removeListener(this.queueReveal)
    window.cancelAnimationFrame(this.revealFrame)
  },
  methods: {
    collectWords() {
      this.wordElements = Array.from(this.$el.querySelectorAll('.signage-reveal__word'))
        .filter(element => element.textContent.trim())
      this.lastProgress = null
    },
    queueReveal() {
      if (this.revealFrame) return
      this.revealFrame = window.requestAnimationFrame(this.updateReveal)
    },
    updateReveal() {
      this.revealFrame = null
      const track = this.$refs.track
      if (!track) return
      const rect = track.getBoundingClientRect()
      const viewport = window.innerHeight
      // Match Vesper's MANIFESTO offsets: start 85%, end 55% of the viewport.
      const distance = rect.height + viewport * 0.3
      const progress = this.motionQuery.matches
        ? 1
        : Math.min(1, Math.max(0, (viewport * 0.85 - rect.top) / distance))
      if (progress === this.lastProgress) return
      this.lastProgress = progress
      this.wordElements.forEach((element, index) => {
        // Overlapping 24% windows soften the sequence instead of wiping across each word.
        const start = (index / this.wordElements.length) * 0.82
        const end = Math.min(start + 0.24, 1)
        const amount = Math.min(1, Math.max(0, (progress - start) / (end - start)))
        element.style.setProperty('--lit', `${amount * 100}%`)
        element.style.setProperty('--word-opacity', 0.35 + amount * 0.65)
        element.style.setProperty('--word-y', `${6 * (1 - amount)}px`)
      })
    }
  }
}
</script>
